import React, { useContext, useMemo } from 'react';
import { TableColumnType } from 'components';
import { AuthContext } from 'contexts';
import { useBreakpoint } from 'hooks';
import { isTeamAdmin as isBBUserTeamAdmin } from '@bb/users';
import { StatusEntry } from '../../../components';
import { useShowingsCalendarContext } from '../ShowingsCalendarContext';
import {
  CommonInnerTableProps,
  InPersonTableData,
  TableRenderFn,
} from '../types';
import { isListingContact, listingAgentNeedsAction } from '../utils';
import { renderCoopContactEntry, buildTableComponents } from './tableUtils';
import {
  ActionsEntry,
  Covid19Entry,
  FeedbackEntry,
  StyledInnerTable,
  TimeEntry,
  StyledMobileColumn,
} from './components';
import { useWriteCalendarHover } from '../ShowingsCalendarHoverContext';

const InPersonTable: React.FC<CommonInnerTableProps<InPersonTableData>> = ({
  tableKey,
  showings,
  setSelectedCovidAppt,
  openAppointmentModal,
}) => {
  const { auth } = useContext(AuthContext);
  const { teams } = auth;
  const { listing, permissionsMap } = useShowingsCalendarContext();
  const { md } = useBreakpoint();
  const { setHoveredAppointment } = useWriteCalendarHover();

  let teamsStrings: string[] = [];
  if (isBBUserTeamAdmin(auth) && teams.length > 0) {
    teamsStrings = teams.map(toString);
  }

  const isTeamAdmin = useMemo(() => (
      auth.team &&
      auth.role === 'TEAM_ADMIN' &&
      listing?.reps.some(
        rep =>
          rep.team?.toString() === auth.team?.toString() ||
          teamsStrings?.includes(rep.team!),
      )
    ), [listing, auth]);

  const isListingAgent = useMemo(() => listing?.reps.some(rep => rep.id === auth.id), [listing, auth]);

  const renderTimeEntry: TableRenderFn<InPersonTableData[number]> = (
    _,
    {
      id,
      passed,
      status,
      time,
      timeEnd,
      listingContactTimeChange,
      listing: { timezone },
    },
    i,
    opts,
  ) => (
    <TimeEntry
      time={time}
      timezone={timezone}
      timeEnd={timeEnd}
      status={status}
      passed={passed}
      isMobile={opts?.isMobile}
      appointmentId={id}
      needsAction={listingAgentNeedsAction({
        status,
        listingContactTimeChange,
      })}
    />
  );

  const renderStatusEntry: TableRenderFn<InPersonTableData[number]> = (
    _,
    { displayStatus, type },
  ) => (
    <StatusEntry isBuyer={false} displayStatus={displayStatus} type={type} />
  );

  const renderSecondaryActionsEntry: TableRenderFn<
    InPersonTableData[number]
  > = (
    _,
    {
      id,
      covid19Overridden,
      covid19RequestedAck,
      covid19Signed,
      feedbackCollected,
      passed,
      signature,
      status,
      timeEnd,
      type,
      listingId,
      orgHasCovidFlag,
    },
    i,
    opts,
  ) => (
    <>
      {orgHasCovidFlag && (
        <Covid19Entry
          isBuyer={false}
          covid19RequestedAck={covid19RequestedAck}
          covid19Signed={covid19Signed}
          covid19Overridden={covid19Overridden}
          signatureType={signature?.template ? 'template' : 'pdf'}
          status={status}
          timeEnd={timeEnd}
          onSignPrompt={() => openAppointmentModal(id, listingId, 'covid')}
          // @ts-ignore BuildJsonType does not go deep enough to support this
          onSignatureView={() => setSelectedCovidAppt(showings[i])}
          isMobile={opts?.isMobile}
        />
      )}
      <FeedbackEntry
        feedbackCollected={feedbackCollected}
        isBuyer={false}
        onClick={() => openAppointmentModal(id, listingId, 'feedback')}
        passed={passed}
        status={status}
        type={type}
        isMobile={opts?.isMobile}
      />
    </>
  );

  const renderPrimaryActions: TableRenderFn<InPersonTableData[number]> = (
    _,
    appointment,
    i,
    opts,
  ) => (
    <ActionsEntry
      canViewDetails={
        auth.roles[auth.role] >= auth.roles.BRANCH_ADMIN ||
        isListingAgent ||
        isTeamAdmin
      }
      hideActions={!(isListingContact(auth.id, listing) || isTeamAdmin)}
      isBuyer={false}
      appointment={appointment}
      permissions={permissionsMap[appointment.id]}
      isMobile={opts?.isMobile}
    />
  );

  const renderMobile: TableRenderFn<InPersonTableData[number]> = (
    _,
    appointment,
    i,
  ) => (
    <StyledMobileColumn>
      {[
        renderStatusEntry(_, appointment, i, { isMobile: true }),
        renderTimeEntry(_, appointment, i, { isMobile: true }),
        renderCoopContactEntry(_, appointment, i, { isMobile: true }),
        renderPrimaryActions(_, appointment, i, { isMobile: true }),
        renderSecondaryActionsEntry(_, appointment, i, { isMobile: true }),
      ]}
    </StyledMobileColumn>
  );

  const columns: TableColumnType<InPersonTableData[number]>[] = md
    ? // Desktop View
      [
        {
          key: 'time',
          dataIndex: '',
          width: 250,
          render: renderTimeEntry,
        },
        {
          key: 'status',
          dataIndex: '',
          width: 250,
          render: renderStatusEntry,
        },
        {
          key: 'coop',
          dataIndex: 'coop',
          width: 250,
          render: renderCoopContactEntry,
        },
        {
          key: 'secondaryActions',
          dataIndex: '',
          width: 200,
          render: renderSecondaryActionsEntry,
        },
        {
          key: 'primaryActions',
          dataIndex: '',
          render: renderPrimaryActions,
        },
      ]
    : // Mobile View
      [
        {
          key: 'mobile',
          dataIndex: '',
          render: renderMobile,
        },
      ];

  return (
    <StyledInnerTable
      key={tableKey}
      components={buildTableComponents({ md })}
      columns={columns}
      rowKey={record => record.id}
      showHeader={false}
      dataSource={showings}
      pagination={false}
      onRow={({ id, listingId }) => ({
          onClick: () => {
            openAppointmentModal(id, listingId, 'history');
          },
          onMouseEnter: () => setHoveredAppointment(id),
          onMouseLeave: () => setHoveredAppointment(null),
        })}
      data-cy="in-person-table"
    />
  );
};

export default InPersonTable;
